import { forwardRef, useEffect, useId, useState } from 'react';
import { MoreHoriz } from '@mui/icons-material';
import {
  Box,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  TextField,
  TextFieldProps,
  Typography,
  useForkRef,
} from '@mui/material';
import { NoteType } from '@/@types/note';
import { useActiveNotesByType } from '@/modules/notes/hooks';

const VISIBLE_PRESET_COUNT = 3;

type NotePresetSelectorProps = Omit<
  TextFieldProps,
  'InputLabelProps' | 'InputProps' | 'multiline' | 'onSelect'
> & {
  type: NoteType;
  disabled?: boolean;
  // eslint-disable-next-line no-unused-vars
  onPresetSelect: (content: string) => void;
};

export const NotePresetSelector = forwardRef<
  HTMLInputElement,
  NotePresetSelectorProps
>(function NotePresetSelector(
  {
    type,
    disabled = false,
    onPresetSelect,
    inputRef: callerInputRef,
    ...textFieldProps
  },
  ref
) {
  const handleInputRef = useForkRef(ref, callerInputRef);
  const { data, isLoading, isError } = useActiveNotesByType(type);
  const [moreAnchorEl, setMoreAnchorEl] = useState<HTMLElement | null>(null);
  const moreMenuId = useId();
  const notes = data?.notes || [];
  const visibleNotes = notes.slice(0, VISIBLE_PRESET_COUNT);
  const hiddenNotes = notes.slice(VISIBLE_PRESET_COUNT);
  const isPresetSelectionDisabled = disabled || isError;
  let presetStatusMessage: string | null = null;

  if (isLoading) {
    presetStatusMessage = 'Carregando observações.';
  } else if (isError) {
    presetStatusMessage = 'Não foi possível carregar as observações.';
  } else if (!notes.length) {
    presetStatusMessage = 'Nenhuma observação cadastrada para este tipo.';
  }

  useEffect(() => {
    if (isPresetSelectionDisabled) setMoreAnchorEl(null);
  }, [isPresetSelectionDisabled]);

  function handlePresetSelect(content: string) {
    if (isPresetSelectionDisabled) return;

    onPresetSelect(content);
    setMoreAnchorEl(null);
  }

  return (
    <>
      <TextField
        {...textFieldProps}
        inputRef={handleInputRef}
        disabled={disabled}
        multiline
        InputLabelProps={{ shrink: true }}
        InputProps={{
          endAdornment: (
            <Box
              aria-label="Usar observação"
              role="group"
              sx={{
                alignItems: 'center',
                borderTop: 1,
                borderColor: 'divider',
                display: 'flex',
                gap: 0.5,
                px: 1,
                py: 0.75,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flex: 1,
                  gap: 0.5,
                  minWidth: 0,
                  overflowX: 'auto',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                }}
              >
                <Box
                  aria-atomic="true"
                  aria-live="polite"
                  role="status"
                  sx={{
                    border: 0,
                    clip: 'rect(0 0 0 0)',
                    height: '1px',
                    m: '-1px',
                    overflow: 'hidden',
                    p: 0,
                    position: 'absolute',
                    whiteSpace: 'nowrap',
                    width: '1px',
                  }}
                >
                  {presetStatusMessage}
                </Box>
                {isLoading ? <CircularProgress size={18} /> : null}
                {isError ? (
                  <Typography color="error" variant="caption">
                    Não foi possível carregar as observações.
                  </Typography>
                ) : null}
                {!isLoading && !isError && !notes.length ? (
                  <Typography color="text.secondary" variant="caption">
                    Nenhuma observação cadastrada para este tipo
                  </Typography>
                ) : null}
                {visibleNotes.map((note) => (
                  <Chip
                    key={note.id}
                    disabled={isPresetSelectionDisabled}
                    label={note.title}
                    size="small"
                    onClick={() => handlePresetSelect(note.content)}
                  />
                ))}
              </Box>
              {hiddenNotes.length ? (
                <Chip
                  aria-controls={moreAnchorEl ? moreMenuId : undefined}
                  aria-expanded={moreAnchorEl ? 'true' : undefined}
                  aria-haspopup="menu"
                  disabled={isPresetSelectionDisabled}
                  label={
                    <Box
                      component="span"
                      sx={{
                        alignItems: 'center',
                        display: 'inline-flex',
                        gap: 0.25,
                      }}
                    >
                      Mais
                      <MoreHoriz fontSize="small" />
                    </Box>
                  }
                  size="small"
                  onClick={(event) => setMoreAnchorEl(event.currentTarget)}
                />
              ) : null}
            </Box>
          ),
          sx: {
            alignItems: 'stretch',
            flexDirection: 'column',
            p: 0,
            '& .MuiInputBase-inputMultiline': {
              boxSizing: 'border-box',
              px: 1.75,
              py: 1.5,
            },
          },
        }}
      />
      <Menu
        id={moreMenuId}
        anchorEl={moreAnchorEl}
        open={Boolean(moreAnchorEl) && !isPresetSelectionDisabled}
        onClose={() => setMoreAnchorEl(null)}
      >
        {hiddenNotes.map((note) => (
          <MenuItem
            disabled={isPresetSelectionDisabled}
            key={note.id}
            onClick={() => handlePresetSelect(note.content)}
          >
            {note.title}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
});
